import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import {
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
} from "../../store/api";

const initialFormState = {
  email: "",
  displayName: "",
  phone: "",
  password: "",
  isPlatformAdmin: false,
  status: "Active",
  mustChangePassword: true,
};

const UserForm = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(userId);

  const { data: user } = useGetUserQuery(userId, { skip: !isEditing });
  const [createUser, createState] = useCreateUserMutation();
  const [updateUser, updateState] = useUpdateUserMutation();

  const [form, setForm] = useState(initialFormState);

  useEffect(() => {
    if (isEditing && user) {
      setForm({
        email: user.email || "",
        displayName: user.displayName || "",
        phone: user.phone || "",
        password: "",
        isPlatformAdmin: user.isPlatformAdmin || false,
        status: user.status || "Active",
        mustChangePassword: user.mustChangePassword || false,
      });
    }
  }, [isEditing, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await updateUser({ userId, payload }).unwrap();
      } else {
        await createUser(form).unwrap();
      }
      navigate("/users");
    } catch (err) {
      // error handled by UI
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? "Edit User" : "Create User"}
        subtitle={
          isEditing ? "Update user information" : "Add a new user to the system"
        }
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate("/users")}
          >
            Back
          </Button>
        }
      />

      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Section title="User Information">
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Email"
                name="email"
                type="email"
                placeholder="user@example.com"
                value={form.email}
                onChange={handleChange}
                required
              />
              <Input
                label="Display Name"
                name="displayName"
                placeholder="John Doe"
                value={form.displayName}
                onChange={handleChange}
                required
              />
              <Input
                label="Phone"
                name="phone"
                placeholder="+1-555-123-4567"
                value={form.phone}
                onChange={handleChange}
              />
              <Select
                label="Status"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </Select>
            </div>
          </Section>

          <Section title="Security">
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label={
                  isEditing
                    ? "New Password (leave blank to keep current)"
                    : "Password"
                }
                name="password"
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required={!isEditing}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Checkbox
                label="Platform Admin"
                name="isPlatformAdmin"
                checked={form.isPlatformAdmin}
                onChange={handleChange}
                helper="Grant full platform access"
              />
              <Checkbox
                label="Must Change Password"
                name="mustChangePassword"
                checked={form.mustChangePassword}
                onChange={handleChange}
                helper="User must change password on next login"
              />
            </div>
          </Section>

          {(createState.isSuccess || updateState.isSuccess) && (
            <Alert tone="success">
              User {isEditing ? "updated" : "created"} successfully.
            </Alert>
          )}
          {(createState.isError || updateState.isError) && (
            <Alert tone="error">
              {createState.error?.data?.detail ||
                updateState.error?.data?.detail ||
                "Failed to save user."}
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={createState.isLoading || updateState.isLoading}
            >
              {isEditing ? "Update" : "Create"} User
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/users")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default UserForm;
